import { useState, useEffect, useMemo } from 'react';
import {
  LayoutDashboard, Calculator, Newspaper, UserRound, LogOut,
  Lock, Download, Crown, TrendingDown, ExternalLink,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { supabase } from '../supabaseClient';
import { Logo } from '../components/Chrome';

// ---------------------------------------------------------------------------
// Demo cost model data (real Supabase data integration is a planned feature)
// ---------------------------------------------------------------------------
const CHEMISTRY_COSTS = {
  NMC811: {
    base: 95,
    cell: { cathode: 42, anode: 8, electrolyte: 6, separator: 4, currentCollectors: 7, housing: 5, other: 3 },
    cathode: { nickel: 18, manganese: 4, cobalt: 14, lithium: 4, processing: 2 },
  },
  NMC622: {
    base: 88,
    cell: { cathode: 38, anode: 8, electrolyte: 6, separator: 4, currentCollectors: 7, housing: 5, other: 3 },
    cathode: { nickel: 15, manganese: 6, cobalt: 12, lithium: 3.5, processing: 1.5 },
  },
  NMC532: {
    base: 85,
    cell: { cathode: 36, anode: 8, electrolyte: 6, separator: 4, currentCollectors: 7, housing: 5, other: 3 },
    cathode: { nickel: 13, manganese: 7, cobalt: 11, lithium: 3.5, processing: 1.5 },
  },
  LFP: {
    base: 70,
    cell: { cathode: 28, anode: 8, electrolyte: 6, separator: 4, currentCollectors: 7, housing: 5, other: 3 },
    cathode: { iron: 4, phosphate: 8, lithium: 3, carbon: 2, processing: 1 },
  },
  NCA: {
    base: 92,
    cell: { cathode: 40, anode: 8, electrolyte: 6, separator: 4, currentCollectors: 7, housing: 5, other: 3 },
    cathode: { nickel: 16, cobalt: 13, aluminum: 2, lithium: 3.5, processing: 1.5 },
  },
};

const CHART_COLORS = { NMC811: '#10744f', NMC622: '#1f8a70', NMC532: '#5b8c2a', LFP: '#a8730a', NCA: '#a0522d' };
const TIER_NAMES = { free: 'Free', tier1: 'Professional', tier2: 'Enterprise', tier3: 'Research' };
const TIERS = { free: 0, tier1: 1, tier2: 2, tier3: 3 };
const TIER_INFO = {
  free: { name: 'Free', price: '$0', features: ['LFP chemistry', 'Pack-level breakdown', 'Industry news'] },
  tier1: { name: 'Professional', price: '$29', features: ['All chemistries', 'Data export', '12-month trends'] },
  tier2: { name: 'Enterprise', price: '$79', features: ['Everything in Pro', 'Cell-level breakdown', 'Priority support'] },
  tier3: { name: 'Research', price: '$149', features: ['Everything in Enterprise', 'Cathode material breakdown', 'Raw datasets'] },
};

function LockedBox({ tierLabel, onUpgrade }) {
  return (
    <div className="locked-box">
      <Lock size={36} color="var(--faint)" />
      <h3>Locked</h3>
      <p>Upgrade to {tierLabel} to access this breakdown.</p>
      <button className="btn btn-primary" onClick={onUpgrade}>
        <Crown size={16} /> Upgrade
      </button>
    </div>
  );
}

function Bars({ data, unit = '$/kWh' }) {
  const max = Math.max(...data.map(([, v]) => v));
  return (
    <div>
      {data.map(([name, value]) => (
        <div className="bar-row" key={name}>
          <div className="lbl">
            <span className="name">{name.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
            <span className="val">${value.toFixed(2)} <span style={{ color: 'var(--faint)', fontWeight: 400 }}>{unit.replace('$', '')}</span></span>
          </div>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${(value / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard({ currentUser, navigate, handleLogout, setCurrentUser, initialSection = 'overview' }) {
  const [section, setSection] = useState(initialSection);
  const [capacity, setCapacity] = useState(75);
  const [chemistry, setChemistry] = useState(currentUser.subscription === 'free' ? 'LFP' : 'NMC811');
  const [view, setView] = useState('pack');

  const tier = currentUser.subscription;
  const hasAccess = (required) => TIERS[tier] >= TIERS[required];
  const canChem = (c) => c === 'LFP' || hasAccess('tier1');
  const canCell = hasAccess('tier2');
  const canCathode = hasAccess('tier3');
  const canDownload = hasAccess('tier1');

  const goAccount = () => { setSection('account'); navigate('/account'); };

  const historicalData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, index) => {
      const point = { month };
      Object.keys(CHEMISTRY_COSTS).forEach((chem) => {
        const monthsAgo = 11 - index;
        point[chem] = Number((CHEMISTRY_COSTS[chem].base * Math.pow(1.015, monthsAgo)).toFixed(2));
      });
      return point;
    });
  }, []);

  const cfg = CHEMISTRY_COSTS[chemistry];
  const packBreakdown = useMemo(() => ({
    cells: cfg.base * 0.65, bms: cfg.base * 0.12, thermal: cfg.base * 0.08,
    housing: cfg.base * 0.10, assembly: cfg.base * 0.05,
  }), [chemistry]);

  const totalPackCost = capacity * cfg.base;
  const totalCellCost = Object.values(cfg.cell).reduce((a, b) => a + b, 0);
  const totalCathodeCost = Object.values(cfg.cathode).reduce((a, b) => a + b, 0);

  const downloadData = () => {
    if (!canDownload) return;
    const payload = {
      capacity, chemistry,
      timestamp: new Date().toISOString(),
      user: { name: currentUser.name, subscription: tier },
      costs: { totalPackCost, costPerKwh: cfg.base, totalCellCost, totalCathodeCost },
      breakdown: {
        pack: packBreakdown,
        ...(canCell && { cell: cfg.cell }),
        ...(canCathode && { cathode: cfg.cathode }),
      },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ev-battery-cost-${chemistry}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // News
  const [news, setNews] = useState([]);
  useEffect(() => {
    if (section !== 'news' || news.length) return;
    (async () => {
      const { data } = await supabase
        .from('battery_news').select('*')
        .order('published_at', { ascending: false }).limit(12);
      setNews(data || []);
    })();
  }, [section]);

  // Account editing
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: currentUser.name, country: currentUser.country, city: currentUser.city,
  });
  const [accError, setAccError] = useState('');
  const [accLoading, setAccLoading] = useState(false);

  const saveProfile = async () => {
    setAccLoading(true); setAccError('');
    try {
      const { error } = await supabase.from('user_profiles')
        .update(profileForm).eq('user_id', currentUser.id);
      if (error) throw error;
      setCurrentUser({ ...currentUser, ...profileForm });
      setEditing(false);
    } catch (err) { setAccError(err.message); } finally { setAccLoading(false); }
  };

  const upgradeTier = async (newTier) => {
    setAccLoading(true); setAccError('');
    try {
      const { error } = await supabase.from('user_profiles')
        .update({ subscription: newTier }).eq('user_id', currentUser.id);
      if (error) throw error;
      setCurrentUser({ ...currentUser, subscription: newTier });
    } catch (err) { setAccError(err.message); } finally { setAccLoading(false); }
  };

  const NAV = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'costmodel', label: 'Cost model', icon: Calculator },
    { id: 'news', label: 'Market news', icon: Newspaper },
    { id: 'account', label: 'Account', icon: UserRound },
  ];
  const TITLES = { overview: 'Overview', costmodel: 'Cost model', news: 'Market news', account: 'Account' };

  return (
    <div className="shell">
      <aside className="sidebar">
        <Logo onClick={() => navigate('/')} />
        <nav className="side-nav">
          {NAV.map((item) => (
            <button
              key={item.id}
              className={`side-item ${section === item.id ? 'active' : ''}`}
              onClick={() => setSection(item.id)}
            >
              <item.icon size={17} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="side-foot">
          <div className="user-chip">
            <div className="avatar">{currentUser.name.charAt(0).toUpperCase()}</div>
            <div>
              <div className="n">{currentUser.name}</div>
              <div className="e">{currentUser.email}</div>
            </div>
          </div>
          <button className="side-item" onClick={handleLogout}>
            <LogOut size={17} />
            <span>Log out</span>
          </button>
        </div>
      </aside>

      <main className="main">
        <div className="main-head">
          <h1>{TITLES[section]}</h1>
          <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center' }}>
            <span className="badge badge-accent">{TIER_NAMES[tier]} plan</span>
            {section === 'costmodel' && (
              <button className="btn btn-outline btn-sm" disabled={!canDownload} onClick={downloadData}>
                <Download size={15} /> Export {!canDownload && <Lock size={12} />}
              </button>
            )}
          </div>
        </div>

        <div className="main-body">
          {/* ------------- OVERVIEW ------------- */}
          {section === 'overview' && (
            <div className="fade-up">
              <div className="stat-grid">
                <div className="panel stat">
                  <div className="k">Pack cost · {chemistry}</div>
                  <div className="v">${cfg.base}<span className="u"> /kWh</span></div>
                </div>
                <div className="panel stat">
                  <div className="k">Total pack ({capacity} kWh)</div>
                  <div className="v">${totalPackCost.toLocaleString()}</div>
                </div>
                <div className="panel stat">
                  <div className="k">Cell cost {!canCell && <Lock size={12} />}</div>
                  <div className="v">${totalCellCost}<span className="u"> /kWh</span></div>
                </div>
                <div className="panel stat">
                  <div className="k">Cathode cost {!canCathode && <Lock size={12} />}</div>
                  <div className="v">${totalCathodeCost.toFixed(1)}<span className="u"> /kWh</span></div>
                </div>
              </div>

              <div className="panel panel-pad" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                  <h3 style={{ fontSize: '1rem' }}>12-month cost trends</h3>
                  <span className="badge badge-green"><TrendingDown size={12} /> declining</span>
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={historicalData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e6df" />
                    <XAxis dataKey="month" stroke="#8a978f" style={{ fontSize: '0.78rem' }} />
                    <YAxis stroke="#8a978f" style={{ fontSize: '0.78rem' }} unit="" width={42} />
                    <Tooltip
                      contentStyle={{ background: '#ffffff', border: '1px solid #e2e6df', borderRadius: 8, fontSize: '0.82rem', boxShadow: '0 4px 16px rgba(27,39,33,0.08)' }}
                      labelStyle={{ color: '#1b2721', fontWeight: 600 }}
                    />
                    <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                    {Object.keys(CHEMISTRY_COSTS).map((chem) => (
                      <Line key={chem} type="monotone" dataKey={chem}
                        stroke={CHART_COLORS[chem]} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="panel" style={{ overflow: 'hidden' }}>
                <table className="data-table">
                  <thead>
                    <tr><th>Chemistry</th><th>$/kWh</th><th>Cell $/kWh</th><th>Access</th></tr>
                  </thead>
                  <tbody className="mono">
                    {Object.entries(CHEMISTRY_COSTS).map(([chem, c]) => (
                      <tr key={chem}>
                        <td style={{ fontWeight: 600 }}>{chem}</td>
                        <td>${c.base.toFixed(2)}</td>
                        <td>${Object.values(c.cell).reduce((a, b) => a + b, 0).toFixed(2)}</td>
                        <td>
                          {canChem(chem)
                            ? <span className="badge badge-green">included</span>
                            : <span className="badge badge-muted"><Lock size={10} /> Pro</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ------------- COST MODEL ------------- */}
          {section === 'costmodel' && (
            <div className="fade-up">
              <div className="panel panel-pad" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'end' }}>
                  <div>
                    <label>Battery capacity</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <input type="range" min="40" max="150" value={capacity}
                        onChange={(e) => setCapacity(Number(e.target.value))} style={{ flex: 1 }} />
                      <span className="mono" style={{ fontSize: '1.15rem', fontWeight: 600, minWidth: '84px', textAlign: 'right' }}>
                        {capacity} kWh
                      </span>
                    </div>
                  </div>
                  <div>
                    <label>Chemistry</label>
                    <div className="seg">
                      {Object.keys(CHEMISTRY_COSTS).map((chem) => {
                        const locked = !canChem(chem);
                        return (
                          <button key={chem} disabled={locked}
                            className={chemistry === chem ? 'active' : ''}
                            onClick={() => setChemistry(chem)}>
                            {chem} {locked && <Lock size={11} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="stat-grid">
                <div className="panel stat">
                  <div className="k">Total pack cost</div>
                  <div className="v">${totalPackCost.toLocaleString()}</div>
                </div>
                <div className="panel stat">
                  <div className="k">Cost per kWh</div>
                  <div className="v">${cfg.base}</div>
                </div>
                <div className="panel stat">
                  <div className="k">Cell $/kWh {!canCell && <Lock size={12} />}</div>
                  <div className="v">${totalCellCost}</div>
                </div>
                <div className="panel stat">
                  <div className="k">Cathode $/kWh {!canCathode && <Lock size={12} />}</div>
                  <div className="v">${totalCathodeCost.toFixed(1)}</div>
                </div>
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <div className="seg">
                  <button className={view === 'pack' ? 'active' : ''} onClick={() => setView('pack')}>Pack level</button>
                  <button className={view === 'cell' ? 'active' : ''} disabled={!canCell} onClick={() => setView('cell')}>
                    Cell level {!canCell && <Lock size={11} />}
                  </button>
                  <button className={view === 'cathode' ? 'active' : ''} disabled={!canCathode} onClick={() => setView('cathode')}>
                    Cathode {!canCathode && <Lock size={11} />}
                  </button>
                </div>
              </div>

              <div className="panel panel-pad">
                {view === 'pack' && <Bars data={Object.entries(packBreakdown)} />}
                {view === 'cell' && (canCell
                  ? <Bars data={Object.entries(cfg.cell)} />
                  : <LockedBox tierLabel="Enterprise (Tier 2)" onUpgrade={goAccount} />)}
                {view === 'cathode' && (canCathode
                  ? <Bars data={Object.entries(cfg.cathode)} />
                  : <LockedBox tierLabel="Research (Tier 3)" onUpgrade={goAccount} />)}
              </div>
            </div>
          )}

          {/* ------------- NEWS ------------- */}
          {section === 'news' && (
            <div className="fade-up">
              <div className="panel" style={{ overflow: 'hidden' }}>
                {news.length === 0 && (
                  <p style={{ padding: '2rem', color: 'var(--muted)' }}>Loading news…</p>
                )}
                {news.map((a) => (
                  <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer"
                    className="news-row" style={{ padding: '1rem 1.5rem' }}>
                    <div>
                      <div className="t">{a.title}</div>
                      {a.description && (
                        <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                          {a.description.length > 140 ? a.description.slice(0, 140) + '…' : a.description}
                        </div>
                      )}
                    </div>
                    <span className="s" style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      {a.source} <ExternalLink size={12} />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* ------------- ACCOUNT ------------- */}
          {section === 'account' && (
            <div className="fade-up">
              {accError && <div className="alert alert-error">{accError}</div>}

              <div className="panel panel-pad" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div className="avatar" style={{ width: 52, height: 52, fontSize: '1.3rem' }}>
                      {currentUser.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.15rem' }}>{currentUser.name}</h3>
                      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                        Member since {new Date(currentUser.joinDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </p>
                    </div>
                  </div>
                  {!editing
                    ? <button className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>Edit profile</button>
                    : (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
                        <button className="btn btn-primary btn-sm" disabled={accLoading} onClick={saveProfile}>
                          {accLoading ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                  <div className="field">
                    <label>Email</label>
                    <input value={currentUser.email} disabled />
                  </div>
                  <div className="field">
                    <label>Name</label>
                    <input value={profileForm.name} disabled={!editing}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Country</label>
                    <input value={profileForm.country} disabled={!editing}
                      onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>City</label>
                    <input value={profileForm.city} disabled={!editing}
                      onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })} />
                  </div>
                </div>
              </div>

              <h3 style={{ fontSize: '1.05rem', marginBottom: '1rem' }}>Subscription</h3>
              <div className="tier-grid">
                {Object.entries(TIER_INFO).map(([t, info]) => {
                  const current = t === tier;
                  return (
                    <div key={t} className={`panel tier-card ${current ? 'current' : ''}`}>
                      {current && (
                        <span className="badge badge-accent" style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                          current
                        </span>
                      )}
                      <div className="tn">{info.name}</div>
                      <div className="tp mono">{info.price}<span>/mo</span></div>
                      <ul>
                        {info.features.map((f) => <li key={f}>· {f}</li>)}
                      </ul>
                      {!current && (
                        <button className="btn btn-outline btn-block btn-sm" disabled={accLoading}
                          onClick={() => upgradeTier(t)}>
                          {accLoading ? '…' : TIERS[t] > TIERS[tier] ? 'Upgrade' : 'Switch'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
